import styles from "./IconButton.module.scss";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../icons/Icon";
import { PortalTooltip } from "../../tooltip/PortalTooltip";
import { useSafeId } from "../../tooltip/hooks/useSafeId";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";

interface IIconButtonProps {
  className?: string;
  iconType: IconType;
  iconHeight?: string;
  iconWidth?: string;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
  iconColor?: string;
  /** Accessible name for icon-only buttons; also exposes the button role. */
  ariaLabel?: string;
}

export const IconButton = ({
  className,
  iconType,
  iconHeight,
  iconWidth,
  disabled,
  tooltip,
  onClick = () => {},
  iconColor,
  ariaLabel,
}: IIconButtonProps) => {
  const tooltipId = useSafeId();

  const defaultColor =
    iconColor ??
    (disabled ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.85)");

  const icon = (
    <Icon
      className={tooltipId}
      type={iconType}
      height={iconHeight ?? "70%"}
      width={iconWidth ?? "100%"}
      color={defaultColor}
    />
  );

  if (disabled) {
    onClick = () => {};
  }
  // A named icon button announces itself as a button, so it also has to be
  // reachable and activatable from the keyboard.
  return (
    <div
      className={classNames(styles.root, className, {
        [styles.disabled]: disabled,
      })}
      role={ariaLabel ? "button" : undefined}
      aria-label={ariaLabel}
      aria-disabled={ariaLabel && disabled ? true : undefined}
      tabIndex={ariaLabel && !disabled ? 0 : undefined}
      onKeyDown={(event) => {
        if (!ariaLabel || (event.key !== "Enter" && event.key !== " ")) {
          return;
        }

        event.preventDefault();
        onClick();
      }}
      onClick={() => {
        onClick();
      }}
    >
      {icon}
      <RenderWhen condition={!!tooltip && !disabled}>
        <PortalTooltip
          anchorSelect={`.${tooltipId}`}
          place="top"
          className={styles.root_tooltip}
          positionStrategy="fixed"
        >
          <div dangerouslySetInnerHTML={{ __html: tooltip! }} />
        </PortalTooltip>
      </RenderWhen>
    </div>
  );
};
